import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Lock } from "lucide-react";
import { useState } from "react";
import { adminApi, type FeatureFlagRow, type FeatureFlagsState } from "@/api/admin.api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import {
  AUDIENCES,
  describeAudience,
  draftOf,
  draftProblem,
  type FlagDraft,
  isDirty,
  parseEmails,
  PRODUCT_LABEL,
} from "./feature-flags";

const QUERY_KEY = ["admin", "feature-flags"];

const formatWhen = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

/**
 * "Feature Flags": who each product's unreleased work is on for. Off, a list of tester emails, or
 * everyone. The product reads its flags from the shared database within about a minute.
 */
export function FeatureFlags() {
  const { data, isLoading, error } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => adminApi.listFeatureFlags(),
  });

  if (isLoading) return <div>Loading...</div>;
  if (error || !data) {
    return <div className="text-red-600">Couldn't load the feature flags: {String(error)}</div>;
  }

  return (
    <div className="max-w-4xl space-y-6 pb-24">
      <div>
        <h1 className="text-xl font-semibold">Feature Flags</h1>
        <p className="mt-1 text-sm text-gray-600">
          Release switches for new work. <b>Off</b>: nobody sees it. <b>Testers</b>: only the emails
          you list, to try it on real accounts first. <b>Everyone</b>: every user of the product.
          Changes reach users within about a minute; a page that's already open keeps its version
          until it's reloaded. (Billing features are on the Features page.)
        </p>
      </div>

      {!data.canEdit && (
        <div className="flex items-center gap-2 rounded-sm border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <Lock className="h-4 w-4" />
          You can view these flags. Changing them needs a Super Admin or Business Admin.
        </div>
      )}

      {data.flags.length === 0 ? (
        <p className="text-sm text-gray-500">No feature flags yet.</p>
      ) : (
        data.flags.map((flag) => (
          <FlagCard key={`${flag.product}:${flag.key}`} flag={flag} canEdit={data.canEdit} />
        ))
      )}
    </div>
  );
}

function FlagCard({ flag, canEdit }: { flag: FeatureFlagRow; canEdit: boolean }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<FlagDraft>(() => draftOf(flag));
  const [confirming, setConfirming] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  const save = useMutation({
    mutationFn: () =>
      adminApi.updateFeatureFlag(flag, {
        audience: draft.audience,
        testerEmails: parseEmails(draft.testersText).emails,
      }),
    onSuccess: (state: FeatureFlagsState) => {
      qc.setQueryData(QUERY_KEY, state);
      const saved = state.flags.find((f) => f.product === flag.product && f.key === flag.key);
      if (saved) setDraft(draftOf(saved));
      setConfirming(false);
      setJustSaved(true);
    },
  });

  const edit = (next: Partial<FlagDraft>) => {
    setDraft((d) => ({ ...d, ...next }));
    setJustSaved(false);
  };

  const dirty = isDirty(flag, draft);
  const problem = dirty ? draftProblem(draft) : null;
  const locked = !canEdit || save.isPending;
  const { emails, invalid } = parseEmails(draft.testersText);
  const showTesters = draft.audience === "TESTERS" || emails.length > 0;
  const nowLabel = describeAudience(flag.audience, flag.testerEmails.length);
  const nextLabel = describeAudience(draft.audience, emails.length);
  const added = emails.filter((e) => !flag.testerEmails.includes(e));
  const removed = flag.testerEmails.filter((e) => !emails.includes(e));

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle className="text-base">{flag.name}</CardTitle>
          <Badge variant="outline">{PRODUCT_LABEL[flag.product]}</Badge>
          <Badge
            variant={
              flag.audience === "EVERYONE"
                ? "success"
                : flag.audience === "TESTERS"
                ? "warning"
                : "secondary"
            }
          >
            {nowLabel}
          </Badge>
        </div>
        <code className="text-xs text-gray-500">{flag.key}</code>
        {flag.description && <CardDescription>{flag.description}</CardDescription>}
      </CardHeader>
      <CardContent className="space-y-4">
        <div
          role="radiogroup"
          aria-label={`Who sees ${flag.name}`}
          className="grid gap-2 sm:grid-cols-3"
        >
          {AUDIENCES.map((option) => {
            const checked = draft.audience === option.value;
            return (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={checked}
                disabled={locked}
                onClick={() => edit({ audience: option.value })}
                className={cn(
                  "rounded-md border px-3 py-2 text-left text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60",
                  checked
                    ? "border-gray-900 bg-gray-900 text-white"
                    : "border-gray-300 bg-white hover:bg-gray-50"
                )}
              >
                <div className="font-medium">{option.label}</div>
                <div className={cn("text-xs", checked ? "text-gray-300" : "text-gray-500")}>
                  {option.hint}
                </div>
              </button>
            );
          })}
        </div>

        {showTesters && (
          <div className="space-y-1">
            <label htmlFor={`testers-${flag.key}`} className="text-sm font-medium">
              Tester emails
            </label>
            <Textarea
              id={`testers-${flag.key}`}
              value={draft.testersText}
              disabled={locked}
              rows={4}
              placeholder={"you@example.com\nteammate@example.com"}
              onChange={(e) => edit({ testersText: e.target.value })}
            />
            <p className="text-xs text-gray-500">
              The emails of their {PRODUCT_LABEL[flag.product]} accounts, one per line.{" "}
              {emails.length} {emails.length === 1 ? "tester" : "testers"}. Kept when you switch to
              Everyone or Off.
            </p>
            {invalid.length > 0 && (
              <p className="text-xs text-red-600">Not an email: {invalid.join(", ")}</p>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-3">
          <span className="text-xs text-gray-500">
            {flag.lastChange
              ? `Last changed by ${flag.lastChange.by} on ${formatWhen(flag.lastChange.at)}.`
              : "Not changed since setup."}
          </span>
          {justSaved && !dirty && (
            <span role="status" className="flex items-center gap-1 text-sm text-green-700">
              <CheckCircle2 className="h-4 w-4" />
              Saved. Users see it within about a minute.
            </span>
          )}
          {dirty && (
            <div className="flex items-center gap-2">
              {/* A bad email is already named under the list; don't say it twice. */}
              {problem && invalid.length === 0 && (
                <span className="text-xs text-red-600">{problem}</span>
              )}
              <Button
                variant="outline"
                size="sm"
                disabled={locked}
                onClick={() => setDraft(draftOf(flag))}
              >
                Discard
              </Button>
              <Button
                size="sm"
                disabled={locked || problem !== null}
                onClick={() => setConfirming(true)}
              >
                Review and save
              </Button>
            </div>
          )}
        </div>
      </CardContent>

      <Dialog open={confirming} onOpenChange={(open) => !save.isPending && setConfirming(open)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change “{flag.name}”?</DialogTitle>
            <DialogDescription>
              Takes effect for {PRODUCT_LABEL[flag.product]} users within about a minute. Pages
              already open keep their version until they're reloaded.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <p>
              <span className="text-gray-500">From</span> {nowLabel}{" "}
              <span className="text-gray-500">to</span> <b>{nextLabel}</b>
            </p>
            {draft.audience === "TESTERS" && added.length > 0 && (
              <p>
                <span className="text-gray-500">Adding:</span> {added.join(", ")}
              </p>
            )}
            {draft.audience === "TESTERS" && removed.length > 0 && (
              <p>
                <span className="text-gray-500">Removing:</span> {removed.join(", ")}
              </p>
            )}
            {save.error && <p className="text-red-600">Couldn't save: {String(save.error)}</p>}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirming(false)}
              disabled={save.isPending}
            >
              Cancel
            </Button>
            <Button onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
